import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { NUMBERS } from "@/data/cases";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";

const CAPTIONS = {
  "Recovered for Policyholders": "Across the firm's Florida practice.",
  "Claims Represented": "Residential · Commercial · Association.",
  "Years of Florida Practice": "Built on documented methodology, not personality.",
  "Launch Markets — Statewide": "Florida HQ · expanding across 33 U.S. states.",
};

function useCountUp(target, start) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 2200;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 4);
      setValue(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, start]);
  return value;
}

function Counter({ entry, start, index }) {
  const value = useCountUp(entry.value, start);
  const display = entry.value >= 1000 ? value.toLocaleString() : value;
  const testid = `number-${entry.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const caption = CAPTIONS[entry.label] || "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.12 }}
      className="group relative"
      data-testid={testid}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-[0.6rem] text-gold tracking-[0.32em]">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-gold/30 via-white/5 to-transparent" />
      </div>
      <div className="font-display-tight gold-metallic text-[clamp(3.5rem,7.5vw,7rem)] leading-[0.92] tracking-[-0.055em]">
        {entry.prefix || ""}
        {display}
        {entry.suffix || ""}
      </div>
      <div className="mt-6 font-mono text-[0.62rem] text-white/80 tracking-[0.28em] uppercase">
        {entry.label}
      </div>
      {caption && (
        <p className="mt-3 text-ag-text-muted text-[0.85rem] leading-[1.65] font-light max-w-[24ch]">
          {caption}
        </p>
      )}
    </motion.div>
  );
}

export default function ByTheNumbers() {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setStart(true), { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="numbers-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-gradient"
    >
      <AmbientLight size={1200} color="blue" intensity={0.4} style={{ top: "-10%", left: "50%", transform: "translateX(-50%)" }} />
      <AmbientLight size={620} color="gold" intensity={0.18} style={{ bottom: "10%", right: "5%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-4">
            <ChapterMark index="06" label="By the Numbers" className="mb-10" />
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,4.4vw,4.25rem)] tracking-[-0.04em] leading-[1]">
              Data,
              <br />
              <span className="gold-metallic font-display font-light">not adjectives.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
            <p className="mt-10 text-ag-text-secondary text-base lg:text-[1.02rem] leading-[1.7] font-light max-w-sm">
              The firm publishes the figures that policyholders can verify — recovered dollars,
              files represented, and operating tenure. Marketing language is left out.
            </p>
            <p className="mt-10 font-mono text-[0.6rem] text-ag-text-muted tracking-[0.3em] uppercase">
              Verified · Anonymized · Florida Practice
            </p>
          </div>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-16 lg:gap-y-20">
              {NUMBERS.map((n, i) => (
                <Counter key={n.label} entry={n} start={start} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
